/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { useRef, useState } from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { useHasBeenOnScreen } from "~/lib/use-has-been-on-screen";
import { Picture } from "~/components/ui/picture";

import ActiveTripExample from "~/components/landing/bento/ActiveTripExample";
import WeatherAlertExample from "~/components/landing/bento/WeatherAlertExample";
import RainDropEffect from "~/components/landing/bento/RainDropEffect";
import LocationAnimation from "~/components/landing/bento/LocationAnimation";
import GeoZonesAnimation from "~/components/landing/bento/GeoZonesAnimation";
import BentoToggleIcon from "~/components/landing/bento/BentoToggleIcon";
import LottieAnimation from "~/components/landing/bento/LottieAnimation";
import DrivingHistoryList from "~/components/landing/bento/DrivingHistoryList";
import TamperDetectionExample from "~/components/landing/bento/TamperDetectionExample";
import MaintenanceExample from "~/components/landing/bento/MaintenanceExample";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const crashedCar = imageUrl("/images/landing/car-impact-white@2x.png");
const accidentDetectionNotification =
  imageUrl("/images/landing/accident-detection-notification.png");
const drivingHabitsAnimationBackground =
  imageUrl("/images/landing/driving-habits-background.jpg");

/**
 * Ported from bouncieweb's `Bento/HighlightsGridTuro.js` /
 * `Bento/HighlightsGridLoaner.js` — a single shared component, because those two
 * files (and `HighlightsGridNascar.js`) are identical apart from six strings.
 *
 * The 7-item animated "bento" grid used by the vehicle-monitoring landing pages:
 * Location, Vehicle Monitoring, Driving History, Habits Revealed, Accident
 * Detection, Weather Alerts, Geo-Zones. Faithful reproduction of layout,
 * structure, and animations; the per-page copy comes in as props.
 *
 * Same shape as the /connecting-family `HighlightsGrid`, except this variant
 * swaps Speed/Curfew for a combined "Vehicle Monitoring" tile (speed + tamper +
 * maintenance) and uses the flat `DrivingHistoryList` instead of the carousel.
 *
 * Grid: 2 columns on mobile with hand-tuned per-item heights (many in vw), then a
 * 3-column × 6-row template at lg+. Expressed here in a scoped <style> keyed off
 * `.mhg-hl-*` item classes so the exact row/column spans survive.
 *
 * `RainDropEffect` keeps all of its WebGL work inside a client-only effect
 * (raindrop-fx crashes SSR at module-eval), which is the equivalent of the
 * original's `next/dynamic(..., { ssr: false })` wrapper.
 */

// ---------------------------------------------------------------------------
// BentoComponents.js — shared primitives
// ---------------------------------------------------------------------------

// GridItem base: 20px radius, clipped, relative, responsive padding.
function gridItemBase(extra?: string) {
  return cn(
    "relative overflow-hidden rounded-[20px] p-6 md:p-8 lg:p-9 xl:p-10",
    extra,
  );
}

function BentoGridH3Large({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className={cn(
        "font-display relative pr-8 leading-[1.15em] font-extrabold",
        "text-[9vw] min-[431px]:text-[6vw] sm:text-[33px] lg:text-[38px] xl:text-[40px]",
      )}
    >
      {children}
    </h3>
  );
}

function BentoGridH3({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h3
      className={cn(
        "font-display text-[22px] font-extrabold md:text-[29px]",
        className,
      )}
    >
      {children}
    </h3>
  );
}

function BentoGridSubtitleP({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <p className={cn("relative m-0 text-[21px]", className)} style={style}>
      {children}
    </p>
  );
}

// Overlay used by 5 of the items — frosted blue panel that fades in on toggle.
function Overlay({
  show,
  children,
}: {
  show: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "absolute inset-0 flex flex-col justify-center p-8 text-white",
        "[background:rgba(31,105,242,0.87)] [backdrop-filter:blur(10px)]",
        "transition-[opacity] duration-300 ease-out",
        show
          ? "pointer-events-auto opacity-100"
          : "pointer-events-none opacity-0",
      )}
    >
      {children}
    </div>
  );
}

function OverlaySubtitleP({ children }: { children: React.ReactNode }) {
  return (
    <p className="relative m-0 [font-family:var(--font-sans)] text-[28px] leading-[1.2] font-bold md:text-[32px]">
      {children}
    </p>
  );
}

function CustomP({ children }: { children: React.ReactNode }) {
  return <p className="mt-2 mb-0 text-[16px] md:text-[18px]">{children}</p>;
}

// VehicleMonitoringItem's inner labels: `{ marginBottom: 8, marginTop: 24 }`.
function MonitoringLabel({ children }: { children: React.ReactNode }) {
  return <div className="mt-6 mb-2">{children}</div>;
}

// ---------------------------------------------------------------------------
// HighlightsGridTuro
// ---------------------------------------------------------------------------

/**
 * The only things that differ between the Turo, ShopLoaner and Nascar versions of
 * this grid. Everything else — headings, layout, animations — is shared.
 */
export interface MonitoringHighlightsCopy {
  /** Location tile subtitle, e.g. "Real-time visibility." */
  locationSubtitle: React.ReactNode;
  locationBody: React.ReactNode;
  habitsOverlayTitle: React.ReactNode;
  habitsBody: React.ReactNode;
  accidentBody: React.ReactNode;
  weatherBody: React.ReactNode;
  geoZonesBody: React.ReactNode;
}

export default function MonitoringHighlightsGrid({
  locationSubtitle,
  locationBody,
  habitsOverlayTitle,
  habitsBody,
  accidentBody,
  weatherBody,
  geoZonesBody,
}: MonitoringHighlightsCopy) {
  // Overlay open/close state
  const [showLocationOverlay, setShowLocationOverlay] = useState(false);
  const [showDrivingOverlay, setShowDrivingOverlay] = useState(false);
  const [showAccidentOverlay, setShowAccidentOverlay] = useState(false);
  const [showWeatherAlertsOverlay, setShowWeatherAlertsOverlay] =
    useState(false);
  const [showGeoZonesOverlay, setShowGeoZonesOverlay] = useState(false);

  // Scroll-into-view animation triggers
  const locationRef = useRef<HTMLDivElement>(null);
  const locationHasBeenOnScreen = useHasBeenOnScreen(locationRef);

  const drivingHabitsRef = useRef<HTMLDivElement>(null);
  const drivingHabitsHasBeenOnScreen = useHasBeenOnScreen(drivingHabitsRef);

  const drivingHistoryRef = useRef<HTMLDivElement>(null);

  const accidentDetectionRef = useRef<HTMLDivElement>(null);
  const accidentDetectionHasBeenOnScreen = useHasBeenOnScreen(
    accidentDetectionRef,
  );

  const geoZonesRef = useRef<HTMLDivElement>(null);
  const geoZonesHasBeenOnScreen = useHasBeenOnScreen(geoZonesRef);

  return (
    <>
      <style>{GRID_STYLES}</style>

      <div className="mhg-highlights-grid">
        {/* ---------------------------------------------------------------- */}
        {/* 1. LOCATION — dark blue gradient */}
        {/* ---------------------------------------------------------------- */}
        <div
          ref={locationRef}
          className={gridItemBase(
            "mhg-hl-location mhg-dark-gradient text-white",
          )}
        >
          <LocationAnimation animate={locationHasBeenOnScreen} />
          <BentoGridH3Large>Location</BentoGridH3Large>
          <BentoGridSubtitleP style={{ color: colors.blueLight5 }}>
            {locationSubtitle}
          </BentoGridSubtitleP>

          <Overlay show={showLocationOverlay}>
            <div className="max-w-[32rem]">
              <OverlaySubtitleP>Know where. Always.</OverlaySubtitleP>
              <CustomP>{locationBody}</CustomP>
            </div>
          </Overlay>
          <BentoToggleIcon
            sid="location"
            clickHandler={() => setShowLocationOverlay((v) => !v)}
            toggleOn={showLocationOverlay}
            className="trigger-gtm-location-overlay"
          />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 2. VEHICLE MONITORING — speed + tamper + maintenance */}
        {/* ---------------------------------------------------------------- */}
        <div
          className={cn(
            "mhg-hl-vehicle-monitoring relative overflow-hidden rounded-[20px] text-center text-[18px]",
            "p-3 min-[431px]:p-4 md:pt-2 lg:pt-3 xl:pt-8",
          )}
          style={{ background: colors.blueLight3 }}
        >
          <BentoGridH3 className="mb-1">Vehicle Monitoring</BentoGridH3>
          <MonitoringLabel>Speed</MonitoringLabel>
          <ActiveTripExample />
          <MonitoringLabel>Tamper Detection</MonitoringLabel>
          <TamperDetectionExample />
          <MonitoringLabel>Maintenance</MonitoringLabel>
          <MaintenanceExample />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 3. DRIVING HISTORY — flat trip list */}
        {/* ---------------------------------------------------------------- */}
        <div
          ref={drivingHistoryRef}
          className={gridItemBase(
            "mhg-hl-driving-history text-center lg:p-3",
          )}
          style={{ background: colors.blueLight3 }}
        >
          <BentoGridH3 className="mb-[34px]">Driving History</BentoGridH3>
          <DrivingHistoryList />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 4. HABITS REVEALED — lottie overlay + bg image */}
        {/* ---------------------------------------------------------------- */}
        <div
          ref={drivingHabitsRef}
          className={gridItemBase(
            "mhg-hl-driving-habits flex flex-col justify-end",
          )}
          style={{ background: colors.blueLight3 }}
        >
          {/* DrivingHabitsAnimationWrapper */}
          <div
            className={cn(
              "pointer-events-none absolute right-[-10px] bottom-[-6px] h-fit",
              "w-[135%] min-[431px]:w-[105%]",
              "sm:right-0",
              "md:w-[85%] md:[mask-image:linear-gradient(90deg,transparent,black_10%)]",
              "lg:bottom-[-6px] lg:w-[50rem] lg:[mask-image:none]",
              "xl:w-[50rem]",
            )}
          >
            <Picture
              src={drivingHabitsAnimationBackground}
              alt=""
              className="w-full"
            />
            {drivingHabitsHasBeenOnScreen && (
              <LottieAnimation
                path="/landing/driving-habits-animation.json"
                loop={false}
                styles={{
                  position: "absolute",
                  width: "100%",
                  top: 0,
                  left: 0,
                }}
              />
            )}
          </div>
          <BentoGridH3Large>
            Habits
            <br />
            Revealed
          </BentoGridH3Large>
          <BentoGridSubtitleP>See how they drive.</BentoGridSubtitleP>
          <Overlay show={showDrivingOverlay}>
            <div className="max-w-[32rem]">
              <OverlaySubtitleP>{habitsOverlayTitle}</OverlaySubtitleP>
              <CustomP>{habitsBody}</CustomP>
            </div>
          </Overlay>
          <BentoToggleIcon
            sid="driving-habits"
            clickHandler={() => setShowDrivingOverlay((v) => !v)}
            toggleOn={showDrivingOverlay}
            className="trigger-gtm-driving-habits-overlay"
          />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 5. ACCIDENT DETECTION — animated car + notification */}
        {/* ---------------------------------------------------------------- */}
        <div
          className={gridItemBase(
            "mhg-hl-accident mhg-dark-gradient text-white",
          )}
        >
          <style>{ACCIDENT_STYLES}</style>
          <BentoGridH3Large>Accident Detection</BentoGridH3Large>
          <BentoGridSubtitleP style={{ color: colors.blueLight5 }}>
            Critical Alerts.
            <br />
            Instantly.
          </BentoGridSubtitleP>

          {/* CrashedCarImage — slides in from the right on scroll. The ref
              lives on this wrapper div (not the Picture) because Picture
              doesn't forward refs to its inner <img>. */}
          <div
            ref={accidentDetectionRef}
            className={cn(
              "mhg-crashed-car absolute h-fit max-w-none",
              accidentDetectionHasBeenOnScreen && "mhg-animate-translate",
            )}
          >
            <Picture
              src={crashedCar}
              alt="A car that has been in an accident"
              className="block h-auto w-full max-w-none"
            />
          </div>
          {/* AccidentDetectionNotification — slides in slightly offset */}
          <Picture
            src={accidentDetectionNotification}
            alt="A notification about a detected accident"
            className={cn(
              "mhg-accident-notification absolute h-fit max-w-none",
              accidentDetectionHasBeenOnScreen && "mhg-animate-translate",
            )}
          />

          <Overlay show={showAccidentOverlay}>
            <div className="max-w-[32rem]">
              <OverlaySubtitleP>The moment it happens.</OverlaySubtitleP>
              <CustomP>{accidentBody}</CustomP>
            </div>
          </Overlay>
          <BentoToggleIcon
            sid="accident-detection"
            clickHandler={() => setShowAccidentOverlay((v) => !v)}
            toggleOn={showAccidentOverlay}
            className="trigger-gtm-accident-detection-overlay"
          />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 6. WEATHER ALERTS — raindrop-fx + notification */}
        {/* ---------------------------------------------------------------- */}
        <div
          className={gridItemBase(
            "mhg-hl-weather flex flex-col justify-start lg:justify-center",
          )}
          style={{ background: colors.blueLight3 }}
        >
          <RainDropEffect />
          {/* WeatherAlertsText */}
          <div className="relative">
            <BentoGridH3Large>
              Weather
              <br />
              Alerts
            </BentoGridH3Large>
            <BentoGridSubtitleP>Eyes in the skies.</BentoGridSubtitleP>
          </div>
          <WeatherAlertExample />
          <Overlay show={showWeatherAlertsOverlay}>
            <OverlaySubtitleP>Weather wise.</OverlaySubtitleP>
            <CustomP>{weatherBody}</CustomP>
          </Overlay>
          <BentoToggleIcon
            sid="weather-alerts"
            clickHandler={() => setShowWeatherAlertsOverlay((v) => !v)}
            toggleOn={showWeatherAlertsOverlay}
            className="trigger-gtm-weather-alerts-overlay"
          />
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 7. GEO-ZONES — dark blue gradient + animation */}
        {/* ---------------------------------------------------------------- */}
        <div
          ref={geoZonesRef}
          className={gridItemBase(
            "mhg-hl-geozones mhg-dark-gradient flex flex-col justify-start text-white md:justify-center",
          )}
        >
          {/* GeoZonesAnimationCustom — shifts the animation down/right */}
          <GeoZonesAnimation
            animate={geoZonesHasBeenOnScreen}
            className={cn(
              "top-[8.5rem] right-[-23%]",
              "min-[431px]:top-[8rem] min-[431px]:right-0",
              "sm:top-[8rem] sm:right-0",
              "md:right-0",
              "lg:top-[1rem] lg:right-[6rem]",
            )}
          />
          <BentoGridH3Large>Geo-Zones</BentoGridH3Large>
          <BentoGridSubtitleP style={{ color: colors.blueLight5 }}>
            You pick the zones.
            <br />
            We&rsquo;ll handle the alerts.
          </BentoGridSubtitleP>
          <Overlay show={showGeoZonesOverlay}>
            <div className="max-w-[32rem]">
              <OverlaySubtitleP>In or out. You&rsquo;ll know.</OverlaySubtitleP>
              <CustomP>{geoZonesBody}</CustomP>
            </div>
          </Overlay>
          <BentoToggleIcon
            sid="geo-zones"
            clickHandler={() => setShowGeoZonesOverlay((v) => !v)}
            toggleOn={showGeoZonesOverlay}
            className="trigger-gtm-geo-zones-overlay"
          />
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Grid template + accident keyframes (scoped raw CSS)
// ---------------------------------------------------------------------------

// The dark-blue-gradient background shared by Location / Accident / Geo-Zones.
const DARK_GRADIENT = `linear-gradient(${colors.zachBlack}, ${colors.blueDark4})`;

const GRID_STYLES = `
  .mhg-highlights-grid .mhg-dark-gradient {
    background: ${DARK_GRADIENT};
  }

  .mhg-highlights-grid {
    display: grid;
    gap: 1rem;
    margin-bottom: 5rem;
    grid-template-columns: repeat(2, 1fr);
  }
  .mhg-highlights-grid .mhg-hl-location { grid-column: span 2; height: 85vw; }
  .mhg-highlights-grid .mhg-hl-driving-habits { grid-column: span 2; grid-row: 3/4; height: 80vw; }
  .mhg-highlights-grid .mhg-hl-driving-history { grid-column: span 2; }
  .mhg-highlights-grid .mhg-hl-vehicle-monitoring { grid-column: span 2; }
  .mhg-highlights-grid .mhg-hl-accident { grid-column: span 2; height: 90vw; }
  .mhg-highlights-grid .mhg-hl-weather { grid-column: span 2; height: 20rem; }
  .mhg-highlights-grid .mhg-hl-geozones { grid-column: span 2; height: 90vw; }

  /* xsm 431 */
  @media (min-width: 431px) {
    .mhg-highlights-grid .mhg-hl-location { height: 18rem; }
    .mhg-highlights-grid .mhg-hl-driving-habits { height: 63vw; }
    .mhg-highlights-grid .mhg-hl-accident { height: 70vw; }
    .mhg-highlights-grid .mhg-hl-weather { grid-column: span 2; height: 18rem; }
    .mhg-highlights-grid .mhg-hl-geozones { height: 60vw; }
  }

  /* sm 640 */
  @media (min-width: 640px) {
    .mhg-highlights-grid .mhg-hl-driving-habits { height: 21.5rem; }
    .mhg-highlights-grid .mhg-hl-accident { grid-column: span 2; height: 23rem; }
    .mhg-highlights-grid .mhg-hl-weather { height: 18rem; }
    .mhg-highlights-grid .mhg-hl-geozones { height: 22rem; }
  }

  /* md 768 */
  @media (min-width: 768px) {
    .mhg-highlights-grid { gap: 1.5rem; grid-template-columns: repeat(2, 1fr); }
    .mhg-highlights-grid .mhg-hl-location { height: 23rem; }
    .mhg-highlights-grid .mhg-hl-driving-habits { height: 23rem; }
    .mhg-highlights-grid .mhg-hl-accident { height: 20rem; }
    .mhg-highlights-grid .mhg-hl-weather { height: 15rem; }
    .mhg-highlights-grid .mhg-hl-geozones { height: 20rem; }
  }

  /* lg 1024 */
  @media (min-width: 1024px) {
    .mhg-highlights-grid {
      grid-template-rows: 12rem 13rem 18.5rem 12rem 13rem 13rem;
      grid-template-columns: repeat(3, 1fr);
    }
    .mhg-highlights-grid .mhg-hl-location { grid-row: 1/3; grid-column: 1/3; height: unset; }
    .mhg-highlights-grid .mhg-hl-driving-history { grid-column: unset; height: unset; }
    .mhg-highlights-grid .mhg-hl-driving-habits { grid-row: 3/5; grid-column: 2/4; height: unset; }
    .mhg-highlights-grid .mhg-hl-vehicle-monitoring { grid-row: 1/3; grid-column: unset; height: unset; }
    .mhg-highlights-grid .mhg-hl-accident { grid-row: 4/6; grid-column: unset; height: unset; }
    .mhg-highlights-grid .mhg-hl-weather { grid-column: 2/4; grid-row: unset; height: unset; }
    .mhg-highlights-grid .mhg-hl-geozones { grid-column: span 3; height: unset; }
  }

  /* xlg 1280 */
  @media (min-width: 1280px) {
    .mhg-highlights-grid { grid-template-rows: 12rem 15rem 19rem 13rem 15rem 15rem; }
  }
`;

// Accident Detection: the crashed car and the notification each translate in
// from the right once on screen. Positions differ per breakpoint (verbatim from
// the original CrashedCarImage / AccidentDetectionNotification styled defs).
const ACCIDENT_STYLES = `
  @keyframes mhg-accident-car-translate {
    0%   { transform: translate(30px, 0px); }
    100% { transform: translate(0px, 0px); }
  }
  @keyframes mhg-accident-notif-translate {
    0%   { transform: translate(50px, 0px); }
    100% { transform: translate(0px, 0px); }
  }

  .mhg-crashed-car {
    bottom: -5%;
    width: 100%;
    right: -15%;
    transform: translate(30px, 0px);
  }
  .mhg-crashed-car.mhg-animate-translate {
    animation: mhg-accident-car-translate 3s forwards 1 ease-out;
  }
  @media (min-width: 431px) {
    .mhg-crashed-car { width: 95%; bottom: -3%; right: -18%; }
  }
  @media (min-width: 768px) {
    .mhg-crashed-car { width: 27rem; right: -4rem; }
  }
  @media (min-width: 1024px) {
    .mhg-crashed-car { bottom: -5%; right: -50%; width: 150%; }
  }

  .mhg-accident-notification {
    bottom: 25%;
    width: 65%;
    right: 26%;
    transform: translate(50px, 0px);
  }
  .mhg-accident-notification.mhg-animate-translate {
    animation: mhg-accident-notif-translate 3s forwards 1 ease-out;
  }
  @media (min-width: 431px) {
    .mhg-accident-notification { bottom: 30%; right: 30%; }
  }
  @media (min-width: 640px) {
    .mhg-accident-notification { width: 55%; right: 35%; bottom: 35%; }
  }
  @media (min-width: 768px) {
    .mhg-accident-notification { width: 18rem; bottom: 6.5rem; right: 13.5rem; }
  }
  @media (min-width: 1024px) {
    .mhg-accident-notification { right: 2rem; bottom: 8.5rem; width: 15rem; }
  }
  @media (min-width: 1280px) {
    .mhg-accident-notification { width: 16.2rem; bottom: 9.5rem; }
  }
`;
